import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PluralForm } from '@nimbus-library/features/localization/models/enums/pluralform.enum';
import { NimbusGridDataView } from '@nimbus-library/features/shared/components/nimbus-grid/domain/nimbus-grid-data-view';
import { NimbusGridSort, orderBy } from '@nimbus-library/features/shared/components/nimbus-grid/domain/nimbus-grid-sort';
import { NimbusGridComponent } from '@nimbus-library/features/shared/components/nimbus-grid/nimbus-grid/nimbus-grid.component';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { WindowEventsService } from '@nimbus-library/features/shared/services/window-events.service';
import { BulkArrayValue, BulkValue } from '@nimbus-global/features/bulk-edit/models/bulk-value';
import { BulkAssignedRoleDetails } from '@nimbus-global/features/bulk-edit/models/bulk-user/bulk-user-edit.view-model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IInputComponent } from '../i-input-component';
import { NimbusGridService } from '../../nimbus-grid/services/nimbus-grid.service';
import { IInputAssignRole } from './domain/i-input-assign-role';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-input-assign-role',
  templateUrl: './input-assign-role.component.html',
  styleUrls: ['./input-assign-role.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputAssignRoleComponent),
    },
  ],
})
export class InputAssignRoleComponent extends IInputComponent<IInputAssignRole[]> implements AfterViewInit {
  @Input()
  public templateReference: TemplateRef<any>;

  @Input()
  public hasUpdatePermission: boolean = true;

  @Input()
  public warningMessage: string;

  @Input()
  public container: ElementRef;

  @Input() public isBulk: boolean = false;

  @Input() public bulkValues: BulkArrayValue<BulkAssignedRoleDetails>;

  @Input()
  public allLicensesOptions: IInputAssignRole[] = [];

  @Input()
  public nonRemovableRoleIds: string[] = [];

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-native
  public search: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public edit: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public updateBulk: EventEmitter<IInputAssignRole> = new EventEmitter<IInputAssignRole>();

  @Output()
  public closeOverlay: EventEmitter<void> = new EventEmitter<void>();

  // GRID OPTIONS
  @ViewChild(NimbusGridComponent, { static: true })
  public gridComponent: NimbusGridComponent;

  private maxCustomRolesCount: number = 5;

  public allOptions: IInputAssignRole[] = [];
  public optionsToAdd: IInputAssignRole[] = [];
  public selectedOptions: IInputAssignRole[] = [];

  public ICONS = ICONS;
  public PluralForm = PluralForm;
  public entities$: Observable<NimbusGridDataView>;

  public pageSize$: BehaviorSubject<number>;

  public loading$: Observable<boolean>;
  public sort$: BehaviorSubject<NimbusGridSort[]>;
  public skip$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public valueTrigger$: ReplaySubject<void> = new ReplaySubject(1);

  @Input()
  public set options(options: IInputAssignRole[]) {
    if (options && options.length) {
      this.allOptions = options;
      this.setSelectionByValue(this.value);
    } else {
      this.allOptions = [];
      this.optionsToAdd = [];
      this.selectedOptions = [];
    }
  }

  @Input()
  public get value(): IInputAssignRole[] {
    return this.innerValue;
  }

  public set value(value: IInputAssignRole[]) {
    if (!isEqual(value, this.innerValue)) {
      this.innerValue = value;
      this.propagateChange(value);
      this.setSelectionByValue(value);
    }
  }

  public get skip(): number {
    return this.skip$.value;
  }

  public set skip(value: number) {
    this.skip$.next(value);
  }

  public constructor(
    private readonly changeDetectionRef: ChangeDetectorRef,
    private readonly resizeEventService: WindowEventsService,
    private readonly nimbusGridService: NimbusGridService,
    injector: Injector,
    translateService: TranslateService,
  ) {
    super(changeDetectionRef, injector, translateService);

    this.sort$ = new BehaviorSubject<NimbusGridSort[]>([{ field: 'name', dir: 'asc' }]);
    this.skip$ = new BehaviorSubject<number>(0);

    this.pageSize$ = new BehaviorSubject<number>(1);
  }

  public ngAfterViewInit(): void {
    this.entities$ = this.initializeNimbusGridDataView();

    this.resizeEventService
      .getResize$()
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      .pipe(debounceTime(350), untilDestroyed(this))
      .subscribe(() => {
        this.refreshPageSize();
      });

    setTimeout(() => this.refreshPageSize());
  }

  public setSelectionByValue(value: IInputAssignRole[]): void {
    if (value) {
      const customRolesCount = value.filter((x) => x.isCustomRole).length;
      this.optionsToAdd = [];
      this.allOptions.forEach((item) => {
        const itemResult = value.find((valueItem) => valueItem.id === item.id);
        if (!itemResult) {
          this.optionsToAdd.push({
            ...item,
            organizationUnits: [],
            isDisabled: item.isCustomRole && customRolesCount >= this.maxCustomRolesCount,
          });
        }
      });
    } else {
      this.optionsToAdd = this.allOptions;
    }
    this.optionsToAdd = this.optionsToAdd.filter((x) => !x.isReadonly);
    this.selectedOptions = this.value || [];
    if (!(this.changeDetectionRef as ViewRef).destroyed) {
      this.changeDetectionRef.markForCheck();
      this.valueTrigger$.next();
    }
  }

  public writeValue(value: IInputAssignRole[]): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.setSelectionByValue(value);
    }
    this.changeDetectionRef.markForCheck();
  }

  public insertParameter(item: IInputAssignRole): void {
    const tempValue: IInputAssignRole[] = JSON.parse(JSON.stringify(this.value || []));
    tempValue.unshift(item);

    this.value = tempValue;
    if (this.isBulk) {
      this.updateBulk.emit(item);
    }
    this.changeDetectionRef.markForCheck();
  }

  public onRemoveItemClick(item: IInputAssignRole): void {
    this.value = JSON.parse(JSON.stringify(this.value)).filter((tv) => tv.id !== item.id);
    if (this.isBulk) {
      this.bulkValues.value = this.bulkValues.value.filter((bulkItem) => bulkItem.value.roleId !== item.id);
      this.updateBulk.emit(null);
    }
    this.changeDetectionRef.markForCheck();
  }

  public onEditClick(item: IInputAssignRole): void {
    this.edit.emit(item.id);
  }

  public initializeNimbusGridDataView(): Observable<NimbusGridDataView> {
    return combineLatest([this.valueTrigger$, this.sort$, this.skip$, this.pageSize$]).pipe(
      map(([, sort, skip, pageSize]) => {
        let entityList = orderBy(this.value || [], sort);
        const alignedSkip = this.nimbusGridService.alignSkipToPage(skip, pageSize, entityList.length);
        if (alignedSkip !== skip) {
          this.skip = alignedSkip;
        }
        entityList = entityList.slice(alignedSkip, alignedSkip + pageSize);
        return {
          total: this.value.length,
          data: entityList,
        } as NimbusGridDataView;
      }),
    );
  }

  public sortingChange(event: NimbusGridSort): void {
    this.sort$.next([event]);
  }

  public pagingChange(skip: number): void {
    this.skip = skip;
  }

  public onSearch(val: string): void {
    this.search.emit(val);
  }

  public onClose(): void {
    this.closeOverlay.emit();
  }

  public isEditButtonDisplayed(role: IInputAssignRole): boolean {
    return !role?.isReadonly && this.hasUpdatePermission;
  }

  public getBulkItem(id: string): BulkValue<BulkAssignedRoleDetails> {
    if (!this.isBulk || !this.bulkValues) {
      return null;
    }
    return this.bulkValues.value.find((bulkItem) => bulkItem.value.roleId === id);
  }

  public isBulkReadonlyLocal(id: string): boolean {
    const bulkItem = this.getBulkItem(id);
    if (!bulkItem) {
      return false;
    }
    return this.isNotAvailableItem(id);
  }
  public onApplyItem(item: IInputAssignRole): void {
    if (this.isBulk) {
      this.updateBulk.emit(item);
    }
  }

  public getApplyTooltip(id: string): TranslationKeys | string {
    const dataItem = this.getBulkItem(id);
    if (this.isBulkReadonlyLocal(id)) {
      return '';
    }
    if (dataItem.identical) {
      return TranslationKeys.AdminUserListRolesApplyDisabled;
    }
    return TranslationKeys.AdminUserListRolesApplyToSelected;
  }

  public hasInitiallyEmpty(id: string): boolean {
    const bulkItem = this.getBulkItem(id);
    if (!bulkItem) {
      return false;
    }

    return bulkItem.value.organizationUnits.initiallyEmpty;
  }

  public refreshPageSize(): void {
    const container: HTMLElement = this.container || this.gridComponent.wrapper.nativeElement;
    const gridOffset = 123;
    const gridRowHeight = 37;
    this.pageSize$.next(this.nimbusGridService.calculatePageSize(container.clientHeight, gridOffset, gridRowHeight));
  }

  public isNotAvailableItem(id: string): boolean {
    return this.allLicensesOptions && !this.allLicensesOptions.find((option) => option.id === id);
  }

  public isNonRemovableRoleId(id: string): boolean {
    return this.nonRemovableRoleIds?.includes(id);
  }
}
